import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { forkJoin, Observable, of, timer } from 'rxjs'
import { catchError, map, retry, shareReplay, switchMap, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { BaseError, convertHttpErrorResponse, Http401Error, Http409Error, HttpError } from 'src/utils/errors'
import { resourceTypeDetails } from 'src/utils/resource-types'
import {
  AcceptInviteRequestDto,
  AccessTemplatesDto,
  AccountDto,
  AccountEntitlementTokenDto,
  AccountResourceTypeConfigsDto,
  AppUsageReportDto,
  AccountSubjectDto,
  AccountSubjectGroupDto,
  adaptAccountSubjectDto,
  adaptApplicationDto,
  adaptApplicationIssueDto,
  adaptApplicationRecoveryPointDto,
  adaptApplicationResourcesResponse,
  adaptAppResourceStatusChangesResponse,
  adaptAwsSourceResourceDto,
  adaptBackupResponseDto,
  adaptEntitlementDto,
  adaptFailoverTestResultDto,
  adaptRecoveryPointAttemptDto,
  adaptRecoveryPointDto,
  adaptRefreshSyncSourceDto,
  adaptRestoreResponseDto,
  adaptScanSyncSourceDto,
  adaptStageSyncPairDto,
  adaptSyncPairDto,
  adaptSyncPairIssueDto,
  adaptSyncPairResourceStatusDto,
  adaptSyncSourceDto,
  adaptSyncSourceIssueDto,
  adaptSyncSyncPairDto,
  ApiKeyDto,
  ApplicationDto,
  ApplicationIntegrationDto,
  ApplicationRecoveryPointDto,
  ApplicationType,
  ArnRuleDto,
  AuthenticatedPrincipalDto,
  AuthenticatedSubjectDto,
  AuthenticateResponse,
  AwsAccountDto,
  AwsTargetResourceDto,
  AzureMarketplaceSubscriptionDto,
  BackupRequestDto,
  BackupResponseDto,
  BaseRuleDto,
  BlockedEndpointsResponseDto,
  CreateAccountRequestDto,
  EntitlerDto,
  ExcludeWeakArnRuleDto,
  FailbackRequestDto,
  FailbackSyncPairDto,
  FinishPasswordResetRequestDto,
  GetUserResponseDto,
  IdentityProviderConfigDto,
  IdentityProviderDto,
  IntegrationConfigDto,
  IntegrationDto,
  JobDto,
  ManualResourceTemplateDto,
  MonthlyUsageReportDto,
  MultiAccountFailbackRequestDto,
  MultiAccountFailbackResponseDto,
  NativeAcsRequestDto,
  NativeAcsResponseDto,
  PreviewInviteResponseDto,
  RefreshSyncSourceDto,
  RegionsResponseDto,
  RecoveryReportPdfUrls,
  ReportType,
  ResourceAttributesDto,
  ResourceConfigDto,
  ResourceScope,
  RestoreRequestDto,
  RestoreResponseDto,
  RetentionSettingsDto,
  RoleDto,
  ScanSyncSourceDto,
  ScanSyncSourceRequestDto,
  StageSyncPairDto,
  StartPasswordResetRequestDto,
  SyncPairAccessDto,
  SyncPairDto,
  SyncPairInfo,
  SyncPairResourceConfigDto,
  SyncPairResourceConfigsDto,
  SyncPhaseDto,
  SyncRequestDto,
  SyncSyncPairDto,
  UpdateApplicationRecoveryPointRequestDto,
  UsageReportDto,
  UserAuthenticationResponseDto,
  UserDto,
  UserLoginDto,
  VerifyEmailRequestDto,
} from './dtos'
import { HubspotService } from './hubspot.service'
import { AccessScope } from './access.service'
import { getMockApplicationIssueDtos } from '../shared/fake-issues'
import { ReleaseDetails } from './release-details.service'
import { AlertItem } from '../arpio/arpio-alert/services/alert.service'
import { AwsEventLogsResponse } from '../interfaces'

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  // Cache for resource types by application type
  private resourceTypesCache: Map<ApplicationType | 'all', Observable<Array<{ value: string; displayName: string }>>> =
    new Map()

  constructor(
    private http: HttpClient,
    private hubspot: HubspotService
  ) {}

  buildUrl(pathFrags: Array<string>) {
    let url = environment.apiRoot
    if (url[url.length - 1] !== '/' && pathFrags[0][0] !== '/') {
      url += '/'
    }
    url += pathFrags.join('/')
    return url
  }

  private get<T>(url: string, params: { [param: string]: any } = null) {
    const options: { observe: 'response'; withCredentials: boolean; params?: { [param: string]: string } } = {
      observe: 'response',
      withCredentials: true,
    }
    if (!!params) {
      options.params = params
    }
    const responseObservable = this.http.get<object>(url, options)
    return responseObservable.pipe(
      retry(3),
      this.loadResponseContent<T>(),
      map((response) => response.body),
      catchError((error) => {
        throw convertHttpErrorResponse(error, url)
      })
    )
  }

  private put<T, RT = T>(url: string, data: T, params: { [param: string]: string } = null) {
    const options: { observe: 'response'; withCredentials: boolean; params?: { [param: string]: string } } = {
      observe: 'response',
      withCredentials: true,
    }
    if (!!params) {
      options.params = params
    }
    const responseObservable = this.http.put<object>(url, data, options)
    return responseObservable.pipe(
      retry(3),
      this.loadResponseContent<RT>(),
      map((response) => response.body),
      catchError((error) => {
        throw convertHttpErrorResponse(error, url)
      })
    )
  }

  private post<T, RT = T>(url: string, data: T, params: { [param: string]: string } = null) {
    const options: { observe: 'response'; withCredentials: boolean; params?: { [param: string]: string } } = {
      observe: 'response',
      withCredentials: true,
    }
    if (!!params) {
      options.params = params
    }
    const responseObservable = this.http.post<object>(url, data, options)
    return responseObservable.pipe(
      this.loadResponseContent<RT>(),
      map((response) => response.body),
      catchError((error) => {
        throw convertHttpErrorResponse(error, url)
      })
    )
  }

  private delete(url: string) {
    const options: { observe: 'response'; withCredentials: boolean; params?: { [param: string]: string } } = {
      observe: 'response',
      withCredentials: true,
    }
    const responseObservable = this.http.delete<null>(url, options)
    return responseObservable.pipe(
      retry(3),
      map((response) => response.body),
      catchError((error) => {
        throw convertHttpErrorResponse(error, url)
      })
    )
  }

  /**
   * Return an OperatorFunction that loads HttpResponse content from the
   * response body or possibly a proprietary Arpio header that requires
   * an additional fetch (for very large responses).
   *
   * The function operates on HttpResponse<object> in order to handle
   * large content response bodies, which are simple JSON strings, and
   * cannot necessarily be deserialized into the caller's preferred type.
   * An observed value of the preferred response type is returned, however.
   */
  loadResponseContent<RT>() {
    return switchMap((response: HttpResponse<object>) => {
      const location = response.headers.get('X-Arpio-Content-Location')
      if (location !== null) {
        return this.http.get<RT>(location, { observe: 'response' })
      }
      return of(response as HttpResponse<RT>)
    })
  }

  startAuthFlow(authenticateUrl: string, returnUrl: string) {
    return this.get<AuthenticateResponse>(authenticateUrl, { redirectUrl: returnUrl }).pipe(
      map((authResponse) => authResponse.loginUrl)
    )
  }

  previewInvite(inviteToken: string) {
    const previewInviteUrl = this.buildUrl(['api', 'auth', 'previewInvite'])
    return this.get<PreviewInviteResponseDto>(previewInviteUrl, { inviteToken })
  }

  startAcceptInviteFlow(inviteToken: string, redirectUrl: string) {
    const acceptInviteUrl = this.buildUrl(['api', 'auth', 'acceptInvite'])
    const acceptInviteRequestDto = {
      inviteToken,
      redirectUrl,
    } as AcceptInviteRequestDto
    return this.post<AcceptInviteRequestDto, AuthenticateResponse>(acceptInviteUrl, acceptInviteRequestDto).pipe(
      map((authResponse) => authResponse.loginUrl)
    )
  }

  startMsEntraAppConsentFlow(identityProviderId: string, returnUrl: string) {
    const url = this.buildUrl(['api', 'auth', 'msEntra', 'appConsent'])
    return this.get<AuthenticateResponse>(url, {
      identityProviderId: identityProviderId,
      redirectUrl: returnUrl,
    }).pipe(map((authResponse) => authResponse.loginUrl))
  }

  private _login(
    username: string,
    password: string,
    successCallback: (authResponse: UserAuthenticationResponseDto) => any,
    unsuccessCallback: () => any,
    errorCallback: (error: BaseError) => any
  ) {
    const loginUrl = this.buildUrl(['api', 'users', 'login'])
    const loginDto: UserLoginDto = { email: username, password }

    // this method does not return an observable, and instead invokes callbacks directly.
    // That's so that we can encapsulate the error handling here to differentiate between
    // a bad auth and an actual error.
    return this.post<UserLoginDto, UserAuthenticationResponseDto>(loginUrl, loginDto).subscribe(
      successCallback,
      (error: BaseError) => {
        if (error instanceof Http401Error) {
          // username/password incorrect
          unsuccessCallback()
        } else {
          // some other error occurred
          errorCallback(error)
        }
      }
    )
  }

  private _submitNativeClaims(
    authToken: string,
    nativeAuthToken: string,
    successCallback: (redirectUrl: string) => any,
    errorCallback: (error: BaseError) => any
  ) {
    const nativeAcsRequest: NativeAcsRequestDto = {
      authToken,
      nativeAuthToken,
    }

    const acsUrl = this.buildUrl(['api', 'auth', 'nativeAcs'])
    this.post<NativeAcsRequestDto, NativeAcsResponseDto>(acsUrl, nativeAcsRequest).subscribe(
      (nativeAcsResponse) => successCallback(nativeAcsResponse.redirectUrl),
      errorCallback
    )
  }

  login(
    username: string,
    password: string,
    authToken: string,
    successCallback: (redirectUrl: string) => any,
    unsuccessCallback: () => any,
    errorCallback: (error: BaseError) => any
  ) {
    /**
     * Flows through the native login process, starting by passing the username
     * and password to the native IDP, then taking the token that comes back and
     * presenting it to the Arpio API to create a session.
     */

    return this._login(
      username,
      password,
      (userAuthResponse: UserAuthenticationResponseDto) => {
        this._submitNativeClaims(authToken, userAuthResponse.nativeAuthToken, successCallback, errorCallback)
        // (error) => {
        //   console.error('An error was encountered submitting claims');
        //   console.error(error);
        // });
      },
      unsuccessCallback,
      errorCallback
    )
  }

  private _registerUser(
    userEmail: string,
    displayName: string,
    password: string,
    successCallback: (authResponse: UserAuthenticationResponseDto) => any,
    conflictingUserCallback: () => any,
    errorCallback: (error: BaseError) => any
  ) {
    const registerUserUrl = this.buildUrl(['api', 'users', 'register'])
    const userDto = {
      email: userEmail,
      name: displayName,
      password,
    } as UserDto

    return this.post<UserDto, UserAuthenticationResponseDto>(registerUserUrl, userDto)
      .pipe(
        switchMap((authResponse) =>
          forkJoin([
            of(authResponse),
            // I think it would be cleaner to just call notifyOfNewUser after the post request, and not
            // chain it with observables, since we don't do anything with the hubspot response anyway.
            this.hubspot.notifyOfNewUser$(userEmail, displayName),
          ])
        ),
        // tslint:disable-next-line:variable-name
        map(([authResponse, _hubspotResponse]) => authResponse)
      )
      .subscribe(successCallback, (error: BaseError) => {
        if (error instanceof Http409Error) {
          conflictingUserCallback()
        } else {
          errorCallback(error)
        }
      })
  }

  registerUser(
    userEmail: string,
    displayName: string,
    password: string,
    authToken: string,
    successCallback: (redirectUrl: string) => any,
    conflictingUserCallback: () => any,
    errorCallback: (error: BaseError) => any
  ) {
    /**
     * Flows through the native registration process, starting by submitting new creds
     * to the Native IDP and then forwarding the returned claims to the Native ACS
     * in the Arpio API.
     */
    return this._registerUser(
      userEmail,
      displayName,
      password,
      (userAuthResponse) => {
        this._submitNativeClaims(authToken, userAuthResponse.nativeAuthToken, successCallback, errorCallback)
      },
      conflictingUserCallback,
      errorCallback
    )
  }

  registerForNewAccount(
    userEmail: string,
    displayName: string,
    password: string,
    successCallback: (userAuthResponseDto: UserAuthenticationResponseDto) => any,
    conflictingUserCallback: () => any,
    errorCallback: (error: BaseError) => any
  ) {
    return this._registerUser(userEmail, displayName, password, successCallback, conflictingUserCallback, errorCallback)
  }

  loginForNewAccount(
    userEmail: string,
    password: string,
    successCallback: (userAuthResponseDto: UserAuthenticationResponseDto) => any,
    unsuccessCallback: () => any,
    errorCallback: (error: BaseError) => any
  ) {
    return this._login(userEmail, password, successCallback, unsuccessCallback, errorCallback)
  }

  listAccounts() {
    /**
     * List all accounts that the current user has access to.
     */
    const listAccountsUrl = this.buildUrl(['api', 'accounts'])
    return this.get<Array<AccountDto>>(listAccountsUrl)
  }

  getAccount(accountId: string) {
    const accountUrl = this.buildUrl(['api', 'accounts', accountId])
    return this.get<AccountDto>(accountUrl)
  }

  getJob(accountId: string, jobId: string): Observable<JobDto> {
    // Job IDs may contain slashes — the backend route uses a path converter.
    const jobUrl = this.buildUrl(['api', 'accounts', accountId, 'jobs', jobId])
    return this.get<JobDto>(jobUrl)
  }

  getReleaseDetails(): Observable<ReleaseDetails[]> {
    const releaseDetailsUrl = this.buildUrl(['api', 'info', 'releaseDetails'])
    return this.get<ReleaseDetails[]>(releaseDetailsUrl)
  }

  getAlerts(): Observable<AlertItem[]> {
    const alertDetailsUrl = this.buildUrl(['api', 'info', 'alerts'])
    return this.get<AlertItem[]>(alertDetailsUrl)
  }

  updateAlerts(data: AlertItem[]): Observable<AlertItem[]> {
    const alertDetailsUrl = this.buildUrl(['api', 'info', 'alerts'])
    return this.put<AlertItem[]>(alertDetailsUrl, data)
  }

  fetchFirewallLogs(
    accountId: string,
    applicationId: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
    filterPattern?: string
  ): Observable<AwsEventLogsResponse> {
    const firewallLogsUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'applications',
      applicationId,
      'networkSandbox',
      'logs',
    ])

    const params: { [param: string]: string } = {}
    if (limit !== undefined) params['limit'] = limit.toString()
    if (startTime !== undefined) params['startTime'] = startTime.toString()
    if (endTime !== undefined) params['endTime'] = endTime.toString()
    if (filterPattern) params['filterPattern'] = filterPattern

    return this.get<AwsEventLogsResponse>(firewallLogsUrl, params)
  }

  getNetworkSandboxBlockedIps(
    accountId: string,
    applicationId: string,
    startTime: Date,
    endTime: Date
  ): Observable<BlockedEndpointsResponseDto> {
    const url = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'applications',
      applicationId,
      'networkSandbox',
      'blockedIps',
    ])
    const params = {
      startTime: Math.floor(startTime.getTime() / 1000),
      endTime: Math.floor(endTime.getTime() / 1000),
    }
    return this.get<BlockedEndpointsResponseDto>(url, params)
  }

  getNetworkSandboxBlockedDomains(
    accountId: string,
    applicationId: string,
    startTime: Date,
    endTime: Date
  ): Observable<BlockedEndpointsResponseDto> {
    const url = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'applications',
      applicationId,
      'networkSandbox',
      'blockedDomains',
    ])
    const params = {
      startTime: Math.floor(startTime.getTime() / 1000),
      endTime: Math.floor(endTime.getTime() / 1000),
    }
    return this.get<BlockedEndpointsResponseDto>(url, params)
  }

  createAccount(nativeAuthToken: string, accountName: string, multiAccount?: boolean) {
    const createAccountUrl = this.buildUrl(['api', 'auth', 'createAccount'])

    const createAccountDto = {
      nativeAuthToken,
      account: {
        name: accountName,
      } as AccountDto,
    } as CreateAccountRequestDto

    return this.post<CreateAccountRequestDto, AccountDto>(createAccountUrl, createAccountDto)
  }

  updateAccount(accountId: string, accountName: string) {
    const accountUrl = this.buildUrl(['api', 'accounts', accountId])
    const accountDto = {
      name: accountName,
    } as AccountDto

    return this.put<AccountDto>(accountUrl, accountDto)
  }

  getAccountUiState(accountId: string) {
    const uiStateUrl = this.buildUrl(['api', 'accounts', accountId, 'uiState'])
    return this.get<Record<string, string>>(uiStateUrl)
  }

  updateAccountUiState(accountId: string, uiState: Record<string, string>) {
    const uiStateUrl = this.buildUrl(['api', 'accounts', accountId, 'uiState'])
    return this.put<Record<string, string>, void>(uiStateUrl, uiState)
  }

  submitEntitlementToken(accountId: string, entitlementToken: string) {
    const entitlerUrl = this.buildUrl(['api', 'accounts', accountId, 'entitler'])
    const entitlementTokenDto = {
      entitlementToken,
    } as AccountEntitlementTokenDto

    return this.put<AccountEntitlementTokenDto, null>(entitlerUrl, entitlementTokenDto)
  }

  getEntitlement(accountId: string) {
    const entitlementUrl = this.buildUrl(['api', 'accounts', accountId, 'entitlement'])
    return this.get<object>(entitlementUrl).pipe(map(adaptEntitlementDto))
  }

  // Usage & Billing dashboard (ARP-7657). Gated by Capability.ENABLE_USAGE_REPORTS +
  // READ_BILLABLE_RESOURCE_REPORT — a 403 means the account isn't entitled.
  getUsageReport(accountId: string, month: string) {
    const url = this.buildUrl(['api', 'accounts', accountId, 'usage_report.json'])
    return this.get<UsageReportDto>(url, { month })
  }
  getAppUsageReport(accountId: string, month: string) {
    const url = this.buildUrl(['api', 'accounts', accountId, 'usage_report', 'apps.json'])
    return this.get<AppUsageReportDto>(url, { month })
  }
  /** Multi-month HWM trend. `months` counts billing cycles and is capped at 12 server-side. */
  getMonthlyUsageReport(accountId: string, months: number) {
    const url = this.buildUrl(['api', 'accounts', accountId, 'usage_report', 'monthly'])
    return this.get<MonthlyUsageReportDto>(url, { months: String(months) })
  }

  getEntitler(accountId: string) {
    const entitlerUrl = this.buildUrl(['api', 'accounts', accountId, 'entitler'])
    return this.get<EntitlerDto>(entitlerUrl)
  }

  listApplications(accountId: string) {
    /**
     * List applications within the specified account
     */
    const listApplicationsUrl = this.buildUrl(['api', 'accounts', accountId, 'applications'])
    return this.get<Array<object>>(listApplicationsUrl).pipe(map((objs) => objs.map(adaptApplicationDto)))
  }

  getApplication(accountId: string, applicationId: string) {
    /**
     * Get a specific application's details
     */
    const getApplicationUrl = this.buildUrl(['api', 'accounts', accountId, 'applications', applicationId])
    return this.get<object>(getApplicationUrl).pipe(map((obj) => adaptApplicationDto(obj)))
  }

  createApplication(
    accountId: string,
    applicationType: ApplicationType,
    applicationName: string,
    applicationRpo: number,
    applicationNotificationEmails: Array<string>,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    tagRules: Array<BaseRuleDto>,
    arnRule: ArnRuleDto,
    excludeWeakArnRule: ExcludeWeakArnRuleDto,
    identityProviderId: string = null
  ) {
    /**
     * create a new application
     */
    const createApplicationUrl = this.buildUrl(['api', 'accounts', accountId, 'applications'])
    const selectionRules: Array<BaseRuleDto> = [...tagRules]
    if (arnRule) {
      selectionRules.push(arnRule)
    }
    if (excludeWeakArnRule) {
      selectionRules.push(excludeWeakArnRule)
    }

    const applicationDto: ApplicationDto = {
      accountId,
      name: applicationName,
      type: applicationType,
      rpo: applicationRpo,
      notificationEmails: applicationNotificationEmails,
      sourceEndpointUri,
      targetEndpointUri,
      selectionRules,
      syncPhase: SyncPhaseDto.STANDBY,
      identityProviderId,
    }

    return this.post<ApplicationDto, object>(createApplicationUrl, applicationDto).pipe(
      map((obj) => adaptApplicationDto(obj))
    )
  }

  updateApplication(
    originalDto: ApplicationDto,
    updatedName: string,
    updatedRpo: number,
    updatedNotificationEmails: Array<string>,
    selectionRules: Array<BaseRuleDto>,
    identityProviderId: string = null
  ) {
    // Build the update URL
    const updateApplicationUrl = this.buildUrl([
      'api',
      'accounts',
      originalDto.accountId,
      'applications',
      originalDto.appId,
    ])

    // Prepare the DTO to send
    const updatedDto = {
      ...originalDto,
      name: updatedName,
      rpo: updatedRpo,
      notificationEmails: updatedNotificationEmails,
      selectionRules,
      identityProviderId,
    }

    // Filter out the read-only configs and statuses in the DTO. We don't send these to the server.
    let failoverTestSyncConfig, restoreConfig, backupStatus, restoreStatus, failbackStatus, filteredDto
    ;({ failoverTestSyncConfig, restoreConfig, backupStatus, restoreStatus, failbackStatus, ...filteredDto } =
      updatedDto)

    // Send the PUT request with the filtered DTO
    return this.put<ApplicationDto, object>(updateApplicationUrl, filteredDto).pipe(
      map((obj) => adaptApplicationDto(obj))
    )
  }

  createApplicationIntegration(accountId: string, applicationId: string, integrationId: string) {
    let createUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'applications',
      applicationId,
      'integrations',
      integrationId,
    ])
    return this.put<null, ApplicationIntegrationDto>(createUrl, null)
  }

  deleteApplicationIntegration(accountId: string, applicationId: string, integrationId: string) {
    let deleteUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'applications',
      applicationId,
      'integrations',
      integrationId,
    ])
    return this.delete(deleteUrl)
  }

  listApplicationResources(accountId: string, applicationId: string, scope: ResourceScope) {
    let url = this.buildUrl(['api', 'accounts', accountId, 'applications', applicationId, 'resources', scope])
    return this.get<object>(url).pipe(map((obj) => adaptApplicationResourcesResponse(obj)))
  }

  listApplicationResourceStatusChanges(
    accountId: string,
    applicationId: string,
    scope: ResourceScope,
    nextToken: string
  ) {
    let url =
      this.buildUrl(['api', 'accounts', accountId, 'applications', applicationId, 'resourceStatusChanges', scope]) +
      `?nextToken=${nextToken}`
    return this.get<object>(url).pipe(map((obj) => adaptAppResourceStatusChangesResponse(obj)))
  }

  submitFailoverTestResults(
    accountId: string,
    applicationId: string,
    sourceEndpointUri: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetEndpointUri: string,
    targetAwsAccountId: string,
    targetRegion: string,
    timestamp: Date,
    success: boolean,
    notes: string
  ) {
    const resultsDto = {
      accountId,
      timestamp: timestamp.toISOString(),
      sourceEndpointUri,
      sourceAwsAccountId,
      sourceRegion,
      targetEndpointUri,
      targetAwsAccountId,
      targetRegion,
      appId: applicationId,
      success,
      notes,
    }

    const testResultUrl = this.buildUrl(['api', 'accounts', accountId, 'failoverTestResults'])
    return this.post<object>(testResultUrl, resultsDto).pipe(map((resultDto) => adaptFailoverTestResultDto(resultDto)))
  }

  /**
   * Create a Recovery Test Report for the most recent recovery test that covers ``appIds``.
   * Returns the created report descriptor plus a presigned S3 URL for immediate PDF download.
   *
   * Uses ``?renderPdf=true`` so the server renders the report as part of the create call and
   * returns a presigned URL in the ``downloadUrl`` body field — no follow-up GET needed.
   * That single URL points at a PDF when one variant was requested and at a zip of both PDFs
   * when both were, which keeps the download to one file and avoids the browser's
   * multiple-download permission prompt.
   * Two fallbacks cover API versions that ship before this one: the per-variant ``pdfUrls``
   * map (a variant that wasn't requested comes back null and is stripped here) and, older
   * still, a single URL in the ``X-Arpio-Content-Location`` header.
   * Bypasses the base ``post`` wrapper because the wrapper
   * transparently re-fetches ``X-Arpio-Content-Location`` as JSON, which would be wrong
   * here (the header points at a PDF).
   *
   * @param accountId the Arpio account that owns the recovery
   * @param appIds the applications to include in the report (must all be covered by a
   *               single recovery test)
   * @param recoveryResult user-attested outcome for this test — ``'success'`` or ``'failed'``
   * @param recipients optional email addresses to send the PDF report to as an attachment.
   *                   Empty / omitted -> no email is sent.
   * @param reportTypes which report variants to generate — any of ``'summary'`` and
   *                    ``'detailed'`` (the variant that appends the identified-issues
   *                    tables). Requesting both yields a zip instead of a PDF.
   */
  createRecoveryReport(
    accountId: string,
    appIds: string[],
    recoveryResult: 'success' | 'failed',
    recipients: string[] = [],
    reportTypes: ReportType[] = ['summary'],
    recoveryId?: string
  ): Observable<{ reportId: string; reportUrl: string; downloadUrl?: string; pdfUrls: RecoveryReportPdfUrls }> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'recovery-reports'])
    const body: {
      appIds: string[]
      recoveryResult: 'success' | 'failed'
      recipients?: string[]
      reportTypes: ReportType[]
      recoveryId?: string
    } = {
      appIds,
      recoveryResult,
      reportTypes,
    }
    // Omitted means "the most recent recovery covering appIds"; supplying it reports on
    // one specific (possibly older) recovery.
    if (recoveryId) {
      body.recoveryId = recoveryId
    }
    if (recipients.length > 0) {
      body.recipients = recipients
    }
    return this.http
      .post<{
        reportId: string
        reportUrl: string
        downloadUrl?: string | null
        pdfUrls?: Partial<Record<ReportType, string | null>>
      }>(url, body, {
        observe: 'response',
        withCredentials: true,
        params: { renderPdf: 'true' },
      })
      .pipe(
        map((response) => {
          // A variant that wasn't requested comes back as an explicit null (e.g. a
          // detailed-only report yields `{summary: null, detailed: '...'}`), so drop the
          // unrendered entries and hand callers a map of real URLs only.
          const rendered = Object.entries(response.body?.pdfUrls ?? {}).filter(([, pdfUrl]) => !!pdfUrl) as Array<
            [ReportType, string]
          >
          // Older API versions carried a single URL in a header instead of the body, and only
          // ever rendered the summary variant — keep reading it until the API catches up.
          const legacyUrl = response.headers.get('X-Arpio-Content-Location')
          const pdfUrls: RecoveryReportPdfUrls =
            rendered.length > 0 ? Object.fromEntries(rendered) : legacyUrl ? { summary: legacyUrl } : {}
          const downloadUrl = response.body?.downloadUrl || undefined
          if (!downloadUrl && Object.keys(pdfUrls).length === 0) {
            throw new Error('Recovery report response carried no download URL')
          }
          return { ...response.body!, downloadUrl, pdfUrls }
        }),
        catchError((error) => {
          if (!(error instanceof HttpErrorResponse)) throw error
          throw convertHttpErrorResponse(error, url)
        })
      )
  }

  /**
   * Fetch the rendered PDF for an existing Recovery Test Report.
   *
   * The server re-renders from the report's persisted data on every request.  When an API
   * response bucket is configured it uploads the PDF and returns a presigned URL in the
   * ``X-Arpio-Content-Location`` header with an empty body; with no bucket configured the
   * bytes come back inline instead, so both shapes are handled here.  Bypasses the base
   * ``get`` wrapper for the same reason ``createRecoveryReport`` does: the wrapper
   * re-fetches that header as JSON, which would be wrong because it points at a PDF.
   *
   * @param accountId the Arpio account that owns the report
   * @param reportId the report to render
   * @param reportType which variant to render — ``'summary'`` or ``'detailed'``
   * @return a presigned URL to download the PDF from, or the PDF bytes themselves
   */
  getRecoveryReportPdf(
    accountId: string,
    reportId: string,
    reportType: ReportType
  ): Observable<{ url?: string; blob?: Blob }> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'recovery-reports', `${reportId}.pdf`])
    return this.http
      .get(url, {
        observe: 'response',
        withCredentials: true,
        params: { reportType },
        responseType: 'blob',
      })
      .pipe(
        map((response) => {
          const location = response.headers.get('X-Arpio-Content-Location')
          if (location) {
            return { url: location }
          }
          if (response.body && response.body.size > 0) {
            return { blob: response.body }
          }
          throw new Error('Recovery report response carried no PDF')
        }),
        catchError((error) => {
          if (!(error instanceof HttpErrorResponse)) throw error
          throw convertHttpErrorResponse(error, url)
        })
      )
  }

  listFailoverTestResults(
    accountId: string,
    applicationId: string,
    startTime: Date,
    endTime: Date,
    reverse: boolean,
    limit: number,
    success?: boolean
  ) {
    const testResultsUrl = this.buildUrl(['api', 'accounts', accountId, 'failoverTestResults'])
    const queryArgs = {} as {
      appId?: string
      timestampStart?: string
      timestampEnd?: string
      reverse?: boolean
      limit?: number
      success?: boolean
    }
    if (!!applicationId) {
      queryArgs.appId = applicationId
    }
    if (!!startTime) {
      queryArgs.timestampStart = startTime.toISOString()
    }
    if (!!endTime) {
      queryArgs.timestampEnd = endTime.toISOString()
    }
    if (reverse !== null) {
      queryArgs.reverse = reverse
    }
    if (limit !== null) {
      queryArgs.limit = limit
    }
    if (success != null) {
      queryArgs.success = success
    }
    return this.get<Array<object>>(testResultsUrl, queryArgs).pipe(
      map((objResults) => objResults.map(adaptFailoverTestResultDto))
    )
  }

  deleteApplication(accountId: string, applicationId: string) {
    const applicationUrl = this.buildUrl(['api', 'accounts', accountId, 'applications', applicationId])
    return this.delete(applicationUrl)
  }

  listSyncPairs(accountId: string) {
    /**
     * List all sync pairs on the specified account.
     */
    const syncPairsUrl = this.buildUrl(['api', 'accounts', accountId, 'syncPairs'])
    return this.get<Array<object>>(syncPairsUrl).pipe(
      map((spDtos) => {
        return spDtos.map((obj) => {
          return obj as SyncPairDto
        })
      })
    )
  }

  getSyncPair(accountId: string, sourceEndpointUri: string, targetEndpointUri: string) {
    const syncPairUrl = this.buildUrl(['api', 'accounts', accountId, 'syncPairs', sourceEndpointUri, targetEndpointUri])
    return this.get<object>(syncPairUrl).pipe(map(adaptSyncPairDto))
  }

  listSyncSources(accountId: string) {
    const syncSourcesUrl = this.buildUrl(['api', 'accounts', accountId, 'syncSources'])
    return this.get<Array<object>>(syncSourcesUrl).pipe(map((ssDtos) => ssDtos.map(adaptSyncSourceDto)))
  }

  getSyncSource(accountId: string, endpointUri: string) {
    const syncSourceUrl = this.buildUrl(['api', 'accounts', accountId, 'syncSources', endpointUri])
    return this.get<object>(syncSourceUrl).pipe(map(adaptSyncSourceDto))
  }

  initiateSyncSourceRefresh(accountId: string, endpointUri: string) {
    const syncSourceRefreshUrl = this.buildUrl(['api', 'accounts', accountId, 'syncSources', endpointUri, 'refresh'])
    return this.post<object, null>(syncSourceRefreshUrl, {}).pipe(
      map(adaptRefreshSyncSourceDto),
      catchError((err) => {
        if (environment.serverInAws === false && err instanceof HttpError && err.responseCode >= 500) {
          // Ignore the 500 internal server error that comes back from my local server
          return of({
            now: new Date(),
          } as RefreshSyncSourceDto)
        }
        throw err
      })
    )
  }

  initiateSyncSourceScan(
    accountId: string,
    endpointUri: string,
    additionalIntegrationIds: Set<string>,
    identityProviderId: string = null
  ) {
    const syncSourceRefreshUrl = this.buildUrl(['api', 'accounts', accountId, 'syncSources', endpointUri, 'scan'])

    const scanRequestDto = {
      extraIntegrationIds: Array.from(additionalIntegrationIds),
      identityProviderId: identityProviderId,
    } as ScanSyncSourceRequestDto

    return this.post<ScanSyncSourceRequestDto, object>(syncSourceRefreshUrl, scanRequestDto).pipe(
      map(adaptScanSyncSourceDto),
      catchError((err) => {
        if (environment.serverInAws === false && err instanceof HttpError && err.responseCode >= 500) {
          // Ignore the 500 internal server error that comes back from my local server
          return of({
            now: new Date(),
          } as ScanSyncSourceDto)
        }
        throw err
      })
    )
  }

  listSourceResources(accountId: string, endpointUri: string, containerArn?: string) {
    let syncSourceResourcesUrl = this.buildUrl(['api', 'accounts', accountId, 'syncSources', endpointUri, 'resources'])

    if (containerArn) {
      syncSourceResourcesUrl += '?containerArn=' + encodeURIComponent(containerArn)
    }

    return this.get<Array<object>>(syncSourceResourcesUrl).pipe(
      map((objArray) => objArray.map(adaptAwsSourceResourceDto))
    )
  }

  listTargetResources(accountId: string, sourceEndpointUri: string, targetEndpointUri: string) {
    const syncPairResourcesUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceEndpointUri,
      targetEndpointUri,
      'resources',
    ])

    return this.get<Array<AwsTargetResourceDto>>(syncPairResourcesUrl)
  }

  initiateSyncPairStaging(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string
  ) {
    const stageUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceAwsAccountId,
      sourceRegion,
      targetAwsAccountId,
      targetRegion,
      'stage',
    ])

    return this.post<null, object>(stageUrl, null).pipe(
      map(adaptStageSyncPairDto),
      catchError((err) => {
        if (environment.serverInAws === false && err instanceof HttpError && err.responseCode >= 500) {
          // ignore the 500 internal server error that comes back from my local server
          return of({
            now: new Date(),
          } as StageSyncPairDto)
        }
        throw err
      })
    )
  }

  initiateBackup(accountId: string, appIds: Array<string>) {
    const backupUrl = this.buildUrl(['api', 'accounts', accountId, 'backup'])
    const backupRequestDto: BackupRequestDto = { appIds: appIds }

    return this.post<BackupRequestDto, object>(backupUrl, backupRequestDto).pipe(
      map(adaptBackupResponseDto),
      catchError((err) => {
        if (environment.serverInAws === false && err instanceof HttpError && err.responseCode >= 500) {
          // ignore the 500 internal server error that comes back from my local server
          return of({
            apps: [],
          } as BackupResponseDto)
        }
        throw err
      })
    )
  }

  initiateSyncPairSync(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string,
    applicationSyncPhases: Map<string, SyncPhaseDto> = null,
    recoveryPointId: string = null,
    isRealTime: boolean = false,
    sandboxSettings: [boolean, string[]?, string[]?] = null
  ) {
    const syncUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceAwsAccountId,
      sourceRegion,
      targetAwsAccountId,
      targetRegion,
      'sync',
    ])

    const [enableSandbox, sandboxWhitelist, sandboxAllowedServices] = sandboxSettings || [false, null, null]

    const syncRequestDto: SyncRequestDto = {
      syncPhases: {},
      recoveryPointId,
      // null values tell the server not to change the value.
      enableRealTimeSync: isRealTime ? true : null, // hack -- the server wants null instead of false here.
      enableNetworkSandbox: enableSandbox,
      networkSandboxWhitelist: enableSandbox ? sandboxWhitelist : null,
      networkSandboxAllowedServices: enableSandbox ? sandboxAllowedServices : null,
    }
    // populate syncPhases from the applicationSyncPhases map
    if (!!applicationSyncPhases) {
      applicationSyncPhases.forEach((syncPhase, appId) => {
        syncRequestDto.syncPhases[appId] = syncPhase
      })
    }

    /////// For Testing ////////
    if (environment.fakeSync409) {
      console.warn('Issuing fake 409 response for test purposes.')
      return timer(0).pipe(
        // Use timer to throw this error asynchronously as would happen in a real server call.
        switchMap((_) => {
          throw new Http409Error('This is a fake error to test 409 responses from the server', syncUrl, {})
          // Will never hit this next line, but this convinces the type checker that this function overall always
          // returns the same type
          return this.post<SyncRequestDto, object>(syncUrl, syncRequestDto).pipe(map(adaptSyncSyncPairDto))
        })
      )
    }

    return this.post<SyncRequestDto, object>(syncUrl, syncRequestDto).pipe(
      map(adaptSyncSyncPairDto),
      catchError((err) => {
        if (environment.serverInAws === false && err instanceof HttpError && err.responseCode >= 500) {
          // ignore the 500 internal server error that comes back from my local server
          // because this API tries to launch a step function and I don't have a
          // step function executor on my local machine.
          return of({
            now: new Date(),
          } as SyncSyncPairDto)
        }
        throw err
      })
    )
  }

  initiateRestore(accountId: string, restoreRequestDto: RestoreRequestDto) {
    const restoreUrl = this.buildUrl(['api', 'accounts', accountId, 'restore'])

    return this.post<RestoreRequestDto, object>(restoreUrl, restoreRequestDto).pipe(
      map(adaptRestoreResponseDto),
      catchError((err) => {
        if (environment.serverInAws === false && err instanceof HttpError && err.responseCode >= 500) {
          // ignore the 500 internal server error that comes back from my local server
          return of({
            apps: [],
          } as RestoreResponseDto)
        }
        throw err
      })
    )
  }

  testSyncPairAccess(syncPairInfo: SyncPairInfo) {
    const testAccessUrl = this.buildUrl([
      'api',
      'accounts',
      syncPairInfo.accountId,
      'syncPairs',
      syncPairInfo.sourceEndpointUri,
      syncPairInfo.targetEndpointUri,
      'access',
    ])

    const identityProviderId = syncPairInfo.identityProviderId
    const params = identityProviderId ? { identityProviderId } : {}

    return this.get<SyncPairAccessDto>(testAccessUrl, params)
  }

  testSingleSyncPairAccess(syncPairInfo: SyncPairInfo, accessScope: AccessScope) {
    const testAccessUrl = this.buildUrl([
      'api',
      'accounts',
      syncPairInfo.accountId,
      'syncPairs',
      syncPairInfo.sourceEndpointUri,
      syncPairInfo.targetEndpointUri,
      'access',
    ])

    const params = { scope: accessScope.valueOf() }

    if (syncPairInfo.identityProviderId) {
      params['identityProviderId'] = syncPairInfo.identityProviderId
    }

    return this.get<SyncPairAccessDto>(testAccessUrl, params)
  }

  getAccessTemplates(
    accountId: string,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    identityProviderId?: string
  ) {
    const accessTemplateUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceEndpointUri,
      targetEndpointUri,
      'accessTemplates',
    ])

    const params = identityProviderId ? { identityProviderId } : {}

    return this.get<AccessTemplatesDto>(accessTemplateUrl, params)
  }

  getManualResourceTemplate(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string
  ) {
    const manualTemplateUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceAwsAccountId,
      sourceRegion,
      targetAwsAccountId,
      targetRegion,
      'manualResourceTemplate',
    ])
    return this.get<ManualResourceTemplateDto>(manualTemplateUrl)
  }

  getAwsAccount(accountId: string, awsAccountId: string) {
    const getAwsAccountUrl = this.buildUrl(['api', 'accounts', accountId, 'awsAccounts', awsAccountId])
    return this.get<AwsAccountDto>(getAwsAccountUrl)
  }

  createAwsAccount(accountId: string, awsAccountId: string) {
    const createAwsAccountUrl = this.buildUrl(['api', 'accounts', accountId, 'awsAccounts'])
    const accountDto: AwsAccountDto = {
      awsAccountId,
      name: awsAccountId,
    }
    return this.post<AwsAccountDto>(createAwsAccountUrl, accountDto)
  }

  listAccountSubjects(accountId: string) {
    const accountSubjectsUrl = this.buildUrl(['api', 'accounts', accountId, 'subjects'])
    return this.get<Array<object>>(accountSubjectsUrl).pipe(
      map((accountSubjects) => {
        const adaptedSubjects = accountSubjects.map((subject) => adaptAccountSubjectDto(subject))
        return adaptedSubjects
      }),
      catchError((error) => {
        console.error('❌ API Error: listAccountSubjects', { accountId, error })
        throw error
      })
    )
  }

  listRecoveryPoints$(accountId: string, _protected?: boolean) {
    const listRpsUrl = this.buildUrl(['api', 'accounts', accountId, 'recoveryPoints'])
    const params = {}
    if (_protected != undefined) {
      params['protected'] = _protected.toString()
    }
    return this.get<Array<object>>(listRpsUrl, params).pipe(
      map((rps) => rps.map((rp) => adaptApplicationRecoveryPointDto(rp)))
    )
  }

  createAccountSubject(
    accountId: string,
    emailAddress: string,
    identityProviderId: string,
    roles?: string[],
    name?: string
  ) {
    const accountSubjectUrl = this.buildUrl(['api', 'accounts', accountId, 'subjects'])
    const accountSubjectDto = {
      email: emailAddress,
      identityProviderId,
      name: name || emailAddress,
      roles: roles || [],
    } as AccountSubjectDto
    return this.post<AccountSubjectDto>(accountSubjectUrl, accountSubjectDto).pipe(
      tap((response) => {}),
      catchError((error) => {
        console.error('❌ API Error: createAccountSubject', { accountId, accountSubjectDto, error })
        throw error
      })
    )
  }

  updateAccountSubject(
    accountId: string,
    accountSubjectId: string,
    accountSubjectData: Partial<AccountSubjectDto>
  ): Observable<AccountSubjectDto> {
    const accountSubjectUrl = this.buildUrl(['api', 'accounts', accountId, 'subjects', accountSubjectId])
    return this.put<AccountSubjectDto>(accountSubjectUrl, accountSubjectData).pipe(
      catchError((error) => {
        console.error('❌ API Error: updateAccountSubject', { accountId, accountSubjectId, accountSubjectData, error })
        throw error
      })
    )
  }

  deleteAccountSubject(accountId: string, accountSubjectId: string) {
    const accountSubjectUrl = this.buildUrl(['api', 'accounts', accountId, 'subjects', accountSubjectId])
    return this.delete(accountSubjectUrl)
  }

  createIdentityProvider(account: string, idpType: string, idpName: string, idpConfig: IdentityProviderConfigDto) {
    const idpUrl = this.buildUrl(['api', 'accounts', account, 'identityProviders'])
    const idpDto = {
      accountId: account,
      name: idpName,
      type: idpType,
      config: idpConfig,
    } as IdentityProviderDto
    return this.post<IdentityProviderDto>(idpUrl, idpDto)
  }

  updateIdentityProvider(
    account: string,
    idpType: string,
    idpName: string,
    identityProviderId: string,
    idpConfig: IdentityProviderConfigDto
  ) {
    const idpUrl = this.buildUrl(['api', 'accounts', account, 'identityProviders', identityProviderId])
    const idpDto = {
      accountId: account,
      name: idpName,
      type: idpType,
      config: idpConfig,
    } as IdentityProviderDto
    return this.put<IdentityProviderDto>(idpUrl, idpDto)
  }

  deleteIdentityProvider(accountId: string, id) {
    const deleteUrl = this.buildUrl(['api', 'accounts', accountId, 'identityProviders', id])
    return this.delete(deleteUrl)
  }

  listIdentityProviders(accountId: string) {
    const idpUrl = this.buildUrl(['api', 'accounts', accountId, 'identityProviders'])
    return this.get<Array<IdentityProviderDto>>(idpUrl)
  }

  listIntegrations(accountId: string) {
    const integrationsUrl = this.buildUrl(['api', 'accounts', accountId, 'integrations'])
    return this.get<Array<IntegrationDto>>(integrationsUrl)
  }

  createIntegration(accountId: string, name: string, config: IntegrationConfigDto) {
    const createUrl = this.buildUrl(['api', 'accounts', accountId, 'integrations'])
    const dto = {
      name: name,
      type: config.type,
      config: config,
    } as IntegrationDto

    return this.post<IntegrationDto>(createUrl, dto)
  }

  updateIntegration(accountId: string, integrationId: string, name: string, config: IntegrationConfigDto) {
    const integrationUrl = this.buildUrl(['api', 'accounts', accountId, 'integrations', integrationId])
    const dto = {
      name: name,
      type: config.type,
      config: config,
    } as IntegrationDto

    return this.put<IntegrationDto>(integrationUrl, dto)
  }

  deleteIntegration(accountId: string, integrationId: string) {
    const integrationUrl = this.buildUrl(['api', 'accounts', accountId, 'integrations', integrationId])
    return this.delete(integrationUrl)
  }

  resendInvite(accountId: string, accountSubjectId: string) {
    const reInviteUrl = this.buildUrl(['api', 'accounts', accountId, 'subjects', accountSubjectId, 'invite'])
    return this.post<{}, AccountSubjectDto>(reInviteUrl, {})
  }

  resendVerificationEmail(accountId: string, accountSubjectId: string) {
    const reVerifyUrl = this.buildUrl(['api', 'accounts', accountId, 'subjects', accountSubjectId, 'verify'])
    return this.post<{}, AccountSubjectDto>(reVerifyUrl, {})
  }

  verifyEmail(verifyToken: string) {
    const verifyUrl = this.buildUrl(['api', 'auth', 'verifyEmail'])
    const verifyEmailDto = {
      verifyToken,
    } as VerifyEmailRequestDto

    return this.post<VerifyEmailRequestDto, null>(verifyUrl, verifyEmailDto)
  }

  startResetPassword(email: string) {
    const resetPasswordUrl = this.buildUrl(['api', 'users', 'resetPassword'])
    const resetPasswordDto = {
      email,
    } as StartPasswordResetRequestDto
    return this.post<StartPasswordResetRequestDto, null>(resetPasswordUrl, resetPasswordDto)
  }

  previewResetPassword(passwordResetToken: string) {
    const previewResetPasswordUrl = this.buildUrl(['api', 'users', 'previewResetPassword'])
    return this.get<UserDto>(previewResetPasswordUrl, { passwordResetToken })
  }

  finishResetPassword(passwordResetToken: string, newPassword: string) {
    const finishResetPasswordUrl = this.buildUrl(['api', 'users', 'finishResetPassword'])
    const finishResetPasswordDto = {
      passwordResetToken,
      newPassword,
    } as FinishPasswordResetRequestDto
    return this.post<FinishPasswordResetRequestDto, null>(finishResetPasswordUrl, finishResetPasswordDto)
  }

  /*
   * userId: This is the subjectId returned from getAuthenticatedSubject and other places.
   */
  getUser(userId: string) {
    const getUserUrl = this.buildUrl(['api', 'users', userId])
    return this.get<GetUserResponseDto>(getUserUrl)
  }

  /*
   * Updates a native IdP user's own profile (currently just the display name). Unlike updateAccountSubject,
   * this endpoint actually persists the name. userId is the authenticated user's subjectId (same as getUser).
   */
  updateUser(userId: string, userData: { name?: string }): Observable<GetUserResponseDto> {
    const updateUserUrl = this.buildUrl(['api', 'users', userId])
    // Error conversion is centralized in put(); the toast is raised by handleArpioError at the call site.
    return this.put<{ name?: string }, GetUserResponseDto>(updateUserUrl, userData)
  }

  listSyncPairIssues(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string
  ) {
    const issuesUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceAwsAccountId,
      sourceRegion,
      targetAwsAccountId,
      targetRegion,
      'issues',
    ])
    return this.get<Array<object>>(issuesUrl).pipe(map((issueObjArray) => issueObjArray.map(adaptSyncPairIssueDto)))
  }

  deleteSyncPairIssue(
    accountId: string,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    scope: string,
    issueId: string
  ) {
    const issueUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceEndpointUri,
      targetEndpointUri,
      'issues',
      scope,
      issueId,
    ])
    return this.delete(issueUrl)
  }

  listApplicationIssues(accountId: string, applicationId: string) {
    if (environment.mockIssues) {
      return of(getMockApplicationIssueDtos())
    }
    const issuesUrl =
      this.buildUrl(['api', 'accounts', accountId, 'applications', applicationId, 'issues']) +
      `?includeSyncSourceIssues=true`
    return this.get<Array<object>>(issuesUrl).pipe(map((issueObjArray) => issueObjArray.map(adaptApplicationIssueDto)))
  }

  deleteApplicationIssue(accountId: string, applicationId: string, scope: string, issueId: string) {
    const issueUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'applications',
      applicationId,
      'issues',
      scope,
      issueId,
    ])
    return this.delete(issueUrl)
  }

  listSyncSourceIssues(accountId: string, endpointUri: string) {
    const issuesUrl = this.buildUrl(['api', 'accounts', accountId, 'syncSources', endpointUri, 'issues'])
    return this.get<Array<object>>(issuesUrl).pipe(map((issuesObjArray) => issuesObjArray.map(adaptSyncSourceIssueDto)))
  }

  deleteSyncSourceIssue(accountId: string, endpointUri: string, scope: string, issueId: string) {
    throw new Error("As of now, the server doesn't implement delete for sync source issues.")
    const issueUrl = this.buildUrl(['api', 'accounts', accountId, 'syncSources', endpointUri, 'issues', scope, issueId])
    return this.delete(issueUrl)
  }

  listSyncPairResourceStatus(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string
  ) {
    const statusUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceAwsAccountId,
      sourceRegion,
      targetAwsAccountId,
      targetRegion,
      'resourceStatus',
    ])
    return this.get<Array<object>>(statusUrl).pipe(
      map((statusObjArray) => statusObjArray.map(adaptSyncPairResourceStatusDto))
    )
  }

  listSyncPairRecoveryPoints(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string
  ) {
    const rpUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceAwsAccountId,
      sourceRegion,
      targetAwsAccountId,
      targetRegion,
      'recoveryPoints',
    ])
    return this.get<Array<object>>(rpUrl).pipe(map((rpObjArray) => rpObjArray.map(adaptRecoveryPointDto)))
  }

  listApplicationRecoveryPoints(accountId: string, applicationId: string) {
    const rpUrl = this.buildUrl(['api', 'accounts', accountId, 'applications', applicationId, 'recoveryPoints'])
    return this.get<Array<object>>(rpUrl).pipe(map((rpObjArray) => rpObjArray.map(adaptRecoveryPointDto)))
  }

  listSyncPairRecoveryPointAttempts(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string,
    startTime: Date = null,
    endTime: Date = null
  ) {
    const rpaUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceAwsAccountId,
      sourceRegion,
      targetAwsAccountId,
      targetRegion,
      'recoveryPointAttempts',
    ])
    const rpaArgs = {} as { timestampStart?: string; timestampEnd?: string }
    if (!!startTime) {
      rpaArgs.timestampStart = startTime.toISOString()
    }
    if (!!endTime) {
      rpaArgs.timestampEnd = endTime.toISOString()
    }
    return this.get<Array<object>>(rpaUrl, rpaArgs).pipe(map((rpaObjs) => rpaObjs.map(adaptRecoveryPointAttemptDto)))
  }

  listApplicationRecoveryPointAttempts(
    accountId: string,
    applicationId: string,
    startTime: Date = null,
    endTime: Date = null
  ) {
    const rpaUrl = this.buildUrl(['api', 'accounts', accountId, 'applications', applicationId, 'recoveryPointAttempts'])
    const rpaArgs = {} as { timestampStart?: string; timestampEnd?: string }
    if (!!startTime) {
      rpaArgs.timestampStart = startTime.toISOString()
    }
    if (!!endTime) {
      rpaArgs.timestampEnd = endTime.toISOString()
    }
    return this.get<Array<object>>(rpaUrl, rpaArgs).pipe(map((rpaObjs) => rpaObjs.map(adaptRecoveryPointAttemptDto)))
  }

  private fakeApplicationIntegrationIds = ['one', 'two']

  listApplicationIntegrations(accountId: string, applicationId: string) {
    const appIntegrationsUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'applications',
      applicationId,
      'integrations',
    ])
    return this.get<Array<ApplicationIntegrationDto>>(appIntegrationsUrl)
  }

  getAuthenticatedSubject() {
    const authSubjUrl = this.buildUrl(['api', 'auth', 'authenticatedSubject'])
    return this.get<AuthenticatedSubjectDto>(authSubjUrl)
  }

  getAuthenticatedPrincipal() {
    const authPrincipalUrl = this.buildUrl(['api', 'auth', 'authenticatedPrincipal'])
    return this.get<AuthenticatedPrincipalDto>(authPrincipalUrl)
  }

  logout() {
    const logoutUrl = this.buildUrl(['api', 'auth', 'logout'])
    return this.post<object, null>(logoutUrl, {})
  }

  getRetentionSettings(accountId: string) {
    const retentionSettingsUrl = this.buildUrl(['api', 'accounts', accountId, 'retention'])

    return this.get<RetentionSettingsDto>(retentionSettingsUrl)
  }

  setRetentionSettings(
    accountId: string,
    recentRetained: number,
    daysRetained: number,
    weeksRetained: number,
    monthsRetained: number,
    quartersRetained: number
  ) {
    const retentionSettingsUrl = this.buildUrl(['api', 'accounts', accountId, 'retention'])
    const settingsDto = {
      recentRetained,
      daysRetained,
      weeksRetained,
      monthsRetained,
      quartersRetained,
    } as RetentionSettingsDto

    return this.put<RetentionSettingsDto, null>(retentionSettingsUrl, settingsDto)
  }

  getResourceAttributes(accountId: string) {
    const resourceAttributesUrl = this.buildUrl(['api', 'accounts', accountId, 'resourceAttributes'])

    return this.get<ResourceAttributesDto>(resourceAttributesUrl)
  }

  setResourceAttributes(accountId: string, tags: { [key: string]: string }) {
    const resourceAttributesUrl = this.buildUrl(['api', 'accounts', accountId, 'resourceAttributes'])
    const resourceAttributesDto = {
      tags: tags,
    } as ResourceAttributesDto

    return this.put<ResourceAttributesDto, null>(resourceAttributesUrl, resourceAttributesDto)
  }

  getResourceTypeConfigs(accountId: string) {
    const url = this.buildUrl(['api', 'accounts', accountId, 'resourceTypeConfigs'])
    return this.get<AccountResourceTypeConfigsDto>(url)
  }

  setResourceTypeConfigs(accountId: string, configs: AccountResourceTypeConfigsDto) {
    const url = this.buildUrl(['api', 'accounts', accountId, 'resourceTypeConfigs'])
    return this.put<AccountResourceTypeConfigsDto, null>(url, configs)
  }

  getSyncPairResourceConfig(accountId: string, sourceEndpointUri: string, targetEndpointUri: string, resourceArn) {
    const resConfigUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceEndpointUri,
      targetEndpointUri,
      'resourceConfig',
      resourceArn,
    ])

    return this.get<SyncPairResourceConfigDto>(resConfigUrl)
  }

  setSyncPairResourceConfig(
    accountId: string,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    resourceArn,
    resourceConfig: ResourceConfigDto,
    substituteArn?: string
  ) {
    const resConfigUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      sourceEndpointUri,
      targetEndpointUri,
      'resourceConfig',
      resourceArn,
    ])

    const config = {
      arn: resourceArn,
      substituteArn,
      resourceConfig,
    } as SyncPairResourceConfigDto

    return this.put<SyncPairResourceConfigDto, SyncPairResourceConfigDto>(resConfigUrl, config)
  }

  getSyncPairResourceConfigs(accountId: string, syncPair: SyncPairInfo) {
    const configsUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      syncPair.sourceEndpointUri,
      syncPair.targetEndpointUri,
      'resourceConfig',
    ])

    return this.get<SyncPairResourceConfigDto[]>(configsUrl)
  }

  setSyncPairResourceConfigs(accountId: string, syncPair: SyncPairInfo, configs: SyncPairResourceConfigsDto) {
    const configsUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      syncPair.sourceEndpointUri,
      syncPair.targetEndpointUri,
      'resourceConfigs',
    ])

    return this.put<SyncPairResourceConfigsDto, SyncPairResourceConfigsDto>(configsUrl, configs)
  }

  initiateFailback(accountId: string, syncPair: SyncPairInfo, applications: string[]) {
    const url = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'syncPairs',
      syncPair.sourceEndpointUri,
      syncPair.targetEndpointUri,
      'failback',
    ])
    return this.post<FailbackRequestDto, FailbackSyncPairDto>(url, { appIds: applications } as FailbackRequestDto)
  }

  initiateMultiAccountFailback(accountId: string, applications: string[]) {
    const url = this.buildUrl(['api', 'accounts', accountId, 'failback'])

    let apps = {}
    applications.forEach((app) => (apps[app] = {}))
    const requestDto = {
      apps: apps,
    } as MultiAccountFailbackRequestDto

    return this.post<MultiAccountFailbackRequestDto, MultiAccountFailbackResponseDto>(url, requestDto)
  }

  updateApplicationRecoveryPoint(accountId: string, applicationId: string, recoveryPointId: string, protectedFlag) {
    const updateUrl = this.buildUrl([
      'api',
      'accounts',
      accountId,
      'applications',
      applicationId,
      'recoveryPoints',
      recoveryPointId,
    ])
    const data = {
      protected: protectedFlag,
    } as UpdateApplicationRecoveryPointRequestDto
    return this.put<UpdateApplicationRecoveryPointRequestDto, ApplicationRecoveryPointDto>(updateUrl, data)
  }

  accountAudienceUri(accountId: string): string {
    return this.buildUrl(['api', 'auth', 'saml', accountId])
  }

  globalAudienceUri(): string {
    return this.buildUrl(['api', 'auth', 'saml'])
  }

  samlAcsUrl() {
    return this.buildUrl(['api', 'auth', 'samlAcs'])
  }

  downloadBillableResourceReportCsv(accountId: string) {
    return this.buildUrl(['api', 'accounts', accountId, 'billable_resources.csv'])
  }

  getBillableResourceReport(accountId: string) {
    const billableResourceReportUrl = this.buildUrl(['api', 'accounts', accountId, 'billable_resources.json'])
    return this.get<Array<object>>(billableResourceReportUrl)
  }

  getRegions(): Observable<RegionsResponseDto> {
    const regionsUrl = this.buildUrl(['api', 'info', 'regions.json'])
    return this.get<RegionsResponseDto>(regionsUrl)
  }

  getResourceTypes(applicationType?: ApplicationType): Observable<Array<{ value: string; displayName: string }>> {
    // Check cache first
    const cacheKey = applicationType || 'all'
    if (this.resourceTypesCache.has(cacheKey)) {
      return this.resourceTypesCache.get(cacheKey)!
    }

    // Azure applications derive resource types from resource-types.ts
    if (applicationType === ApplicationType.AZURE) {
      const azureTypes = resourceTypeDetails
        .filter(([typeInfo]) => {
          // Filter for Azure resource types (those that start with "Microsoft.")
          const resourceTypeValue = typeInfo.resourceType as string
          return resourceTypeValue.startsWith('Microsoft.')
        })
        .map(([typeInfo, details]) => ({
          value: typeInfo.resourceType as string,
          displayName: details.displayName,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName))

      const resourceTypes$ = of(azureTypes).pipe(shareReplay(1))
      this.resourceTypesCache.set(cacheKey, resourceTypes$)
      return resourceTypes$
    }

    // Otherwise assume AWS (backwards compatibility - legacy applications are AWS)
    const resourceTypesUrl = this.buildUrl(['api', 'info', 'resourceTypes.json'])
    const resourceTypes$ = this.get<any>(resourceTypesUrl).pipe(
      map((response) => {
        const resourceTypes: Array<{ value: string; displayName: string }> = []

        // Flatten AWS resource types
        if (response.aws) {
          response.aws.forEach((rt: any) => {
            resourceTypes.push({
              value: rt.arpioType,
              displayName: rt.arpioTypeDescription,
            })
          })
        }

        // Flatten Spot by NetApp resource types
        if (response.spotByNetApp) {
          response.spotByNetApp.forEach((rt: any) => {
            resourceTypes.push({
              value: rt.arpioType,
              displayName: rt.arpioTypeDescription,
            })
          })
        }

        // Flatten K8s resource types
        if (response.k8s) {
          response.k8s.forEach((rt: any) => {
            resourceTypes.push({
              value: rt.arpioType,
              displayName: rt.arpioTypeDescription,
            })
          })
        }

        return resourceTypes.sort((a, b) => a.displayName.localeCompare(b.displayName))
      }),
      shareReplay(1)
    )

    // Store in cache
    this.resourceTypesCache.set(cacheKey, resourceTypes$)

    return resourceTypes$
  }

  // Role Management APIs
  listRoles(accountId: string): Observable<RoleDto[]> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'roles'])
    return this.get<RoleDto[]>(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: listRoles', { accountId, error })
        throw error
      })
    )
  }

  getRole(accountId: string, roleId: string): Observable<RoleDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'roles', roleId])
    return this.get<RoleDto>(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: getRole', { accountId, roleId, error })
        throw error
      })
    )
  }

  createRole(accountId: string, role: RoleDto): Observable<RoleDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'roles'])
    return this.post<RoleDto>(url, role).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: createRole', { accountId, roleData: role, error })
        throw error
      })
    )
  }

  updateRole(accountId: string, roleId: string, role: RoleDto): Observable<RoleDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'roles', roleId])
    return this.put<RoleDto>(url, role).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: updateRole', { accountId, roleId, roleData: role, error })
        throw error
      })
    )
  }

  deleteRole(accountId: string, roleId: string): Observable<void> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'roles', roleId])
    return this.delete(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: deleteRole', { accountId, roleId, error })
        throw error
      })
    )
  }

  // Group Management APIs
  listAccountSubjectGroups(accountId: string): Observable<AccountSubjectGroupDto[]> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'subjectGroups'])
    return this.get<AccountSubjectGroupDto[]>(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: listAccountSubjectGroups', { accountId, error })
        throw error
      })
    )
  }

  getAccountSubjectGroup(accountId: string, groupId: string): Observable<AccountSubjectGroupDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'subjectGroups', groupId])
    return this.get<AccountSubjectGroupDto>(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: getAccountSubjectGroup', { accountId, groupId, error })
        throw error
      })
    )
  }

  createAccountSubjectGroup(accountId: string, group: AccountSubjectGroupDto): Observable<AccountSubjectGroupDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'subjectGroups'])
    return this.post<AccountSubjectGroupDto>(url, group).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: createAccountSubjectGroup', { accountId, groupData: group, error })
        throw error
      })
    )
  }

  updateAccountSubjectGroup(
    accountId: string,
    groupId: string,
    group: AccountSubjectGroupDto
  ): Observable<AccountSubjectGroupDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'subjectGroups', groupId])
    return this.put<AccountSubjectGroupDto>(url, group).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: updateAccountSubjectGroup', { accountId, groupId, groupData: group, error })
        throw error
      })
    )
  }

  deleteAccountSubjectGroup(accountId: string, groupId: string): Observable<void> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'subjectGroups', groupId])
    return this.delete(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: deleteAccountSubjectGroup', { accountId, groupId, error })
        throw error
      })
    )
  }

  // API Key Management APIs
  listApiKeys(accountId: string): Observable<ApiKeyDto[]> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'apiKeys'])
    return this.get<ApiKeyDto[]>(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: listApiKeys', { accountId, error })
        throw error
      })
    )
  }

  getApiKey(accountId: string, apiKeyId: string): Observable<ApiKeyDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'apiKeys', apiKeyId])
    return this.get<ApiKeyDto>(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: getApiKey', { accountId, apiKeyId, error })
        throw error
      })
    )
  }

  createApiKey(accountId: string, apiKey: ApiKeyDto): Observable<ApiKeyDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'apiKeys'])
    return this.post<ApiKeyDto>(url, apiKey).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: createApiKey', { accountId, apiKeyData: apiKey, error })
        throw error
      })
    )
  }

  updateApiKey(accountId: string, apiKeyId: string, apiKey: ApiKeyDto): Observable<ApiKeyDto> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'apiKeys', apiKeyId])
    return this.put<ApiKeyDto>(url, apiKey).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: updateApiKey', { accountId, apiKeyId, apiKeyData: apiKey, error })
        throw error
      })
    )
  }

  deleteApiKey(accountId: string, apiKeyId: string): Observable<void> {
    const url = this.buildUrl(['api', 'accounts', accountId, 'apiKeys', apiKeyId])
    return this.delete(url).pipe(
      map((response) => {
        return response
      }),
      catchError((error) => {
        console.error('❌ API Error: deleteApiKey', { accountId, apiKeyId, error })
        throw error
      })
    )
  }

  getAzureMarketplaceSubscription(token: string): Observable<AzureMarketplaceSubscriptionDto> {
    const url = this.buildUrl(['api', 'azureMarketplace', 'subscription'])
    return this.get<AzureMarketplaceSubscriptionDto>(url, { token })
  }

  activateAzureMarketplaceSubscription(token: string): Observable<AccountDto> {
    const url = this.buildUrl(['api', 'azureMarketplace', 'activate'])
    return this.post<null, AccountDto>(url, null, { token })
  }
}
